import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  Container,
  Image,
  IconButton,
  Link,
  Icon
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { useColorModeValue } from './chakra-ui/color-mode';
import { GrClose } from 'react-icons/gr';
import ViewPhotosSwiper from './view-photos-swiper';
import { useParams } from 'react-router-dom';
import { toTitleCase } from '../utils/utils';
import { MdArrowBackIosNew } from 'react-icons/md';
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';
import { COLORS } from '../theme';
import { FONTS } from '../constants/fonts';

function ViewPhotos({ allImages, galleryItems }) {
  const { urlPath } = useParams();
  const images = allImages[urlPath] || [];
  const [isOpen, setIsOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);

  const findTitleFromGalleryItems = (urlPath) => {
    const item = galleryItems.find((item) => item.urlPath === urlPath);
    return item ? item.title : '';
  };
  const title = findTitleFromGalleryItems(urlPath);

  const containerBg = useColorModeValue('white', 'gray.950');
  const secondaryTextColor = useColorModeValue('gray.600', 'gray.400');

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }
    return () => document.body.classList.remove('no-scroll');
  }, [isOpen]);

  const handlePhotoClick = (index) => {
    setSelectedImageIndex(index);
    setIsOpen(true);
  };

  return (
    <Box minH="100vh" bg={containerBg} p={{ base: 4, md: 8 }} id="view-photos">
      <Container maxW="container.xl" p={0}>
        <VStack spacing={8} align="stretch">
          {/* Modern Header Navigation */}
          <VStack align="start" spacing={4} w="full">
            <Link
              as={RouterLink}
              to="/#gallery"
              display="flex"
              alignItems="center"
              fontSize="sm"
              fontWeight="bold"
              color={COLORS.brand.primary}
              gap={2}
              _hover={{ gap: 4 }}
              transition="all 0.3s"
              fontFamily={FONTS.heading}
              textTransform="uppercase"
              letterSpacing="widest"
            >
              <Icon as={MdArrowBackIosNew} />
              Back to Showroom
            </Link>

            <VStack align="start" spacing={2} w="full">
              <Heading
                fontSize={{ base: '3xl', md: '5xl' }}
                fontFamily={FONTS.heading}
                letterSpacing="tight"
              >
                {toTitleCase(title)}
              </Heading>
              <HStack spacing={4}>
                <Box w="40px" h="1px" bg={COLORS.brand.primary} />
                <Text
                  color={secondaryTextColor}
                  fontSize="md"
                  fontWeight="medium"
                >
                  {images.length} Captured Moments
                </Text>
              </HStack>
            </VStack>
          </VStack>

          {/* Masonry Grid */}
          <ResponsiveMasonry
            columnsCountBreakPoints={{ 350: 1, 700: 2, 900: 3, 1200: 4 }}
            style={{ width: '100%' }}
          >
            <Masonry gutter="1.5rem">
              {images.map((image, index) => (
                <Box
                  key={image}
                  borderRadius="2xl"
                  shadow="sm"
                  overflow="hidden"
                  _hover={{
                    transform: 'translateY(-8px)',
                    shadow: '2xl',
                    '& img': { transform: 'scale(1.05)' }
                  }}
                  transition="all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1)"
                  cursor="pointer"
                  onClick={() => handlePhotoClick(index)}
                  bg="gray.100"
                >
                  <Image
                    src={image}
                    alt={`${title} - ${index}`}
                    objectFit="cover"
                    transition="transform 0.6s ease"
                  />
                </Box>
              ))}
            </Masonry>
          </ResponsiveMasonry>
        </VStack>
      </Container>

      {/* Fullscreen Slider Overlay */}
      {isOpen && (
        <Box
          position="fixed"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bg="black"
          zIndex="9999"
        >
          <IconButton
            position="absolute"
            top={4}
            right={4}
            zIndex="10000"
            size="lg"
            onClick={() => setIsOpen(false)}
            aria-label="Close slider"
            variant="ghost"
            color="white"
            _hover={{ bg: 'whiteAlpha.200', transform: 'rotate(90deg)' }}
            transition="all 0.3s"
          >
            <GrClose size={24} />
          </IconButton>
          <ViewPhotosSwiper
            photos={images}
            selectedIndex={selectedImageIndex}
          />
        </Box>
      )}
    </Box>
  );
}

export default ViewPhotos;
